import { Grid, useMediaQuery } from "@mui/material"
import Box from "@mui/material/Box"
import Paper from "@mui/material/Paper"
import SpeedDial from "@mui/material/SpeedDial"
import SpeedDialAction from "@mui/material/SpeedDialAction"
import SpeedDialIcon from "@mui/material/SpeedDialIcon"
import { DataGrid } from "@mui/x-data-grid"
import dayjs from "dayjs"
import { getAnalytics, logEvent } from "firebase/analytics"
import {
  collection,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore"
import { FunctionComponent, useEffect, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { useHistory } from "react-router-dom"
import Color from "../Color"
import { getInvoiceFromDocSnapshot } from "../contexts/InvoiceContext"
import Dimen from "../Dimen"
import { Collection, Field, Route } from "../Enum"
import {
  paperStyleDefault,
  redGreenCellColor,
  useSpeedDialStyle,
} from "../Style"
import { timber_debug } from "../Timber"
import { Buyer, Invoice } from "../Type"
import {
  getInvoiceListColumns,
  getTranslatedDataGridToolbar,
} from "../utils/DataGridUtils"
import { exportInvoicesAsExcel } from "../utils/ExcelUtils"
import { exportInvoiceListAsPdf } from "../utils/PdfUtils"
import { Action, getInvoiceListSpeedDialActions } from "../utils/SpeedDialUtils"

const InvoiceList: FunctionComponent<{ userId: string }> = ({ userId }) => {
  const intl = useIntl()
  const classes = useSpeedDialStyle()
  const minHeight = useMediaQuery("(min-height:700px)")
  const analytics = getAnalytics()
  const history = useHistory()

  const [invoices, setInvoices] = useState<Invoice[]>([])

  // Firestore
  const firestore = getFirestore()
  const invoicesRef = collection(
    firestore,
    `/${Collection.Users}/${userId}/${Collection.Invoices}`
  )
  const invoicesQuery = query(invoicesRef, orderBy(`${Field.Date}`))
  const buyersRef = collection(
    firestore,
    `/${Collection.Users}/${userId}/${Collection.Buyers}`
  )
  const buyersQuery = query(buyersRef, orderBy(`${Field.Name}`))

  const [buyers, setBuyers] = useState<Buyer[]>([])

  /**
   * Gets invoices
   */
  useEffect(() => {
    // Sets up real-time invoice listener
    timber_debug("useEffect subscribeInvoicesListener")
    const unsubscribeInvoicesListener = onSnapshot(
      invoicesQuery,
      (querySnapshot) => {
        const newInvoices: Invoice[] = []
        querySnapshot.forEach((doc) => {
          newInvoices.push(getInvoiceFromDocSnapshot(doc))
        })
        timber_debug(`Downloaded ${newInvoices.length} invoices`)
        setInvoices(newInvoices)
      },
      (error: Error) => {
        console.error("Error fetching invoices", error)
        logEvent(analytics, "web_download_invoices_error")
      }
    )
    return () => {
      timber_debug("useEffect unsubscribeInvoicesListener")
      unsubscribeInvoicesListener()
    }
  }, [])

  /**
   * Gets buyers
   */
  useEffect(() => {
    // Sets up real-time buyers listener
    timber_debug("useEffect subscribeBuyersListener")
    const unsubscribeBuyersListener = onSnapshot(
      buyersQuery,
      (querySnapshot) => {
        const newBuyers: Buyer[] = []
        querySnapshot.forEach((doc) => {
          newBuyers.push({
            id: doc.id,
            name: doc.data().name,
            address: doc.data().address,
            phone: doc.data().phone,
            email: doc.data().email,
            website: doc.data().website,
            insertDate: dayjs().valueOf(),
            lastEditDate: dayjs().valueOf(),
          })
        })
        timber_debug(`Downloaded ${newBuyers.length} buyers`)
        setBuyers(newBuyers)
      },
      (error) => {
        console.error("Error fetching buyers", error)
      }
    )
    return () => {
      timber_debug("useEffect unsubscribeBuyersListener")
      unsubscribeBuyersListener()
    }
  }, [])

  const onSelectInvoice = (invoice: Invoice) => {
    history.push(`${Route.InvoiceList}/${invoice.id}`)
  }

  const openInvoiceDialog = () => {
    // Reset invoice before opening dialog
    history.push(`${Route.InvoiceList}/new`)
  }

  const handleFabAction = (actionId: string) => {
    switch (actionId) {
      case "invoice":
        openInvoiceDialog()
        break
      case "pdf":
        exportInvoiceListAsPdf(invoices, buyers, analytics)
        break
      case "excel":
        exportInvoicesAsExcel(invoices, buyers, analytics)
        break
      default:
        break
    }
  }

  return (
    <Box
      height="91vh"
      display="flex"
      flexDirection="column"
      sx={redGreenCellColor}
    >
      <Paper
        elevation={3}
        style={{
          height: "85vh",
          margin: "8px",
          ...paperStyleDefault,
          padding: "8px",
        }}
      >
        <div style={{ fontSize: Dimen.FontSizeDefault }}>
          <FormattedMessage id="nav.invoice.list" />
        </div>
        <Grid
          container
          spacing={minHeight ? 1 : 0}
          direction={"row"}
          height="85vh"
          marginTop={1}
        >
          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
            {/* Invoice list */}
            <div style={{ height: minHeight ? "85%" : "100%" }}>
              <DataGrid
                localeText={getTranslatedDataGridToolbar(intl)}
                onRowClick={(params) => onSelectInvoice(params.row as Invoice)}
                rows={invoices}
                columns={getInvoiceListColumns(intl, buyers)}
                disableRowSelectionOnClick
                density="standard"
                showToolbar
                pageSizeOptions={[10]}
              />
            </div>
          </Grid>
        </Grid>
        {/* Action button */}
        <SpeedDial
          ariaLabel="InvoiceList SpeedDial"
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
          }}
          icon={<SpeedDialIcon />}
          FabProps={{
            style: { backgroundColor: Color.Red40, color: Color.White },
          }}
        >
          {getInvoiceListSpeedDialActions(intl).map((action: Action) => (
            <SpeedDialAction
              key={action.id}
              icon={action.icon}
              slotProps={{
                tooltip: {
                  title: action.name,
                  open: true,
                },
                fab: {
                  style: {
                    backgroundColor: Color.Primary,
                    color: Color.Black,
                  },
                },
              }}
              classes={classes} // for keep action tooltip text in a single line
              onClick={() => {
                handleFabAction(action.id)
              }}
            />
          ))}
        </SpeedDial>
      </Paper>
    </Box>
  )
}

export default InvoiceList
